import { Store } from "containers/routes";
import { useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import constants from "../constants";

const PATIENT_APPOINTMENT_MODULE_ID = Number(
  Object.keys(constants.ModuleIds).find(
    (moduleId) =>
      constants.ModuleIds[moduleId] === "isPatientAppointmentFeatureEnabled",
  ),
);

/**
 * Plan features of the owner the office belongs to.
 *
 * The subscription slice holds an object with `planFeature` when the logged in
 * user is the account owner, and a list of owner subscriptions when the logged
 * in user is a staff member working for several owners.
 *
 * @param {Object|Array} subscription Subscription redux slice.
 * @param {number} [ownerId] Owner of the office being rendered.
 * @returns {Array|null} Plan features, or null when they are not loaded yet.
 */
const getOwnerPlanFeatures = (subscription, ownerId) => {
  if (subscription?.planFeature) return subscription.planFeature;
  if (!Array.isArray(subscription) || !ownerId) return null;
  return (
    subscription.find((owner) => owner?.ownerId === ownerId)?.planFeature ?? null
  );
};

/**
 * The module stays accessible while the plan features are unknown, and is only
 * treated as unavailable once the API explicitly reports it as unavailable.
 *
 * @param {Array|null} planFeatures
 * @returns {boolean}
 */
const isPatientAppointmentModuleAvailable = (planFeatures) => {
  if (!Array.isArray(planFeatures) || !planFeatures.length) return true;
  const module = planFeatures.find(
    (feature) => feature?.id === PATIENT_APPOINTMENT_MODULE_ID,
  );
  if (!module) return true;
  return module.isAvailable !== false;
};

/**
 * Access rules for the Patient Dashboard link rendered on the office tiles.
 *
 * The link is blocked when the office owner's plan does not include the patient
 * appointments module, or when the viewer can no longer act on that office
 * (inactive office, removed staff member, expired owner package). Blocked links
 * are dimmed and open the upgrade subscription modal instead of navigating.
 *
 * @param {Object} office Office list item.
 * @returns {{ isBlocked: boolean, openUpgradeModal: () => void }}
 */
export default function usePatientDashboardAccess(office) {
  const { setIsSubscriptionModel } = useContext(Store);
  const profile = useSelector((state) => state.userProfile?.profile);
  const subscription = useSelector((state) => state.Subscription);

  const isBlocked = useMemo(() => {
    const isOwner =
      profile?.role?.systemRole === constants.systemRoles.accountOwner;

    const isModuleAvailable = isPatientAppointmentModuleAvailable(
      getOwnerPlanFeatures(subscription, office?.ownerId),
    );

    const isViewerBlocked = isOwner
      ? profile?.isActive === false ||
        !office?.isActive ||
        profile?.profileSetupStep === "packageExpired" ||
        profile?.profileSetupStep === constants.subscriptionTerminated
      : !office?.isActive ||
        !office?.isUserActiveOfficeStaff ||
        !!office?.isUserRemovedFromOffice ||
        !!office?.hasOwnerPackageExpired;

    return !isModuleAvailable || isViewerBlocked;
  }, [
    profile?.role?.systemRole,
    profile?.isActive,
    profile?.profileSetupStep,
    subscription,
    office?.ownerId,
    office?.isActive,
    office?.isUserActiveOfficeStaff,
    office?.isUserRemovedFromOffice,
    office?.hasOwnerPackageExpired,
  ]);

  return {
    isBlocked,
    openUpgradeModal: () => setIsSubscriptionModel(true),
  };
}
